import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Table, Tag, Input, Button, Modal, Checkbox, message } from "antd";
import { SearchOutlined, EditOutlined, CloseOutlined, PlusCircleOutlined } from "@ant-design/icons";
import back_img from "../../assests/images/rightVector.svg";
import Smcs_Loader from "../../assests/images/loader-new.gif";
import AddCircleWhite from "../../assests/images/add-circle-white.svg";
import { getRestrictedHubs, getRestrictedHubsClear } from "../../actionsMethods/roHubAccessMethods";
import API from "../../api";
import urls from "../../api/urls";



const RoHubAccess = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [searchText, setSearchText] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedHubs, setSelectedHubs] = useState([]);

  // States for Editing
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingScreen, setEditingScreen] = useState(null);
  const [editHubs, setEditHubs] = useState([]);
  const [hubSearchText, setHubSearchText] = useState("");

  // States for Hub Selection
  const [isAddHubModalVisible, setIsAddHubModalVisible] = useState(false);
  const [allAvailableHubs, setAllAvailableHubs] = useState([]);
  const [tempSelectedHubs, setTempSelectedHubs] = useState([]);
  const [hubSelectionSearchText, setHubSelectionSearchText] = useState("");


  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
  });

  const { result, fetching } = useSelector(
    (state) => state.roHubAccessReducer.getRestrictedHubs
  );

  const fetchRoHubAccessData = () => {
    const userinfo = localStorage.getItem("userinfo");
    let token = "";
    let userId = "";
    if (userinfo) {
      const parsed = JSON.parse(userinfo);
      token = parsed.accessToken;
      userId = parsed.userId;
    }
    dispatch(getRestrictedHubs({ token, userId }));
  };

  useEffect(() => {
    fetchRoHubAccessData();
    return () => {
      dispatch(getRestrictedHubsClear());
    };
  }, [dispatch]);

  // Fetch all hubs for the Add Hub modal
  const fetchAllHubs = async (search = "", currentFilter = "name") => {
    try {
      let response;
      if (!search) {
        response = await API.Api(urls.GET_HUB_LISTS).getWithHeader();
      } else {
        const params = {
          page: 0,
          size: 1000,
          [currentFilter.toLowerCase()]: search
        };
        response = await API.Api(urls.GET_HUB_LIST_WITH_FILTER).getwithParams(params);
      }

      if (response && response.status && response.response && response.response.data) {
        let rawData = response.response.data;
        let dataList = [];

        if (Array.isArray(rawData)) {
          dataList = rawData;
        } else if (rawData.content && Array.isArray(rawData.content)) {
          dataList = rawData.content;
        } else if (rawData.data && Array.isArray(rawData.data)) {
          dataList = rawData.data;
        }

        const hubs = dataList.map((item) => ({
          id: item.premiseId,
          name: item.premiseName
        }));
        setAllAvailableHubs(hubs);
      }
    } catch (error) {
      console.error("Error fetching hub list:", error);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchAllHubs(hubSelectionSearchText, "name");
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [hubSelectionSearchText]);

  const showAllHubs = (hubs) => {
    setSelectedHubs(hubs);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setSelectedHubs([]);
  };

  // --- Edit Handlers ---
  const showEditModal = (record) => {
    setEditingScreen(record);
    const hubs = record.restricted_hubs ? record.restricted_hubs.map(h => ({
      id: h.premise_id,
      name: h.premise_name
    })) : [];
    setEditHubs(hubs);
    setIsEditModalVisible(true);
  };

  const handleEditCancel = () => {
    setIsEditModalVisible(false);
    setEditingScreen(null);
    setEditHubs([]);
    setHubSearchText("");
  };

  const handleRemoveHub = (hubToRemove) => {
    setEditHubs(editHubs.filter(hub => hub.id !== hubToRemove.id));
  };

  const handleSaveChanges = async () => {
    if (!editingScreen) return;

    try {
      const payload = {
        items: [
          {
            title: editingScreen.title,
            restricted_hub_ids: editHubs.map(h => h.id)
          }
        ]
      };

      const userinfoLocal = JSON.parse(localStorage.getItem("userinfo") || "{}");
      const userId = userinfoLocal?.userId;

      const response = await API.Api(urls.RESTRICTED_HUBS).putCustomHeader(payload, {
        "user-id": userId,
      });

      if (response && response.status) {
        message.success("Restricted Hubs updated successfully");
        setIsEditModalVisible(false);
        setEditingScreen(null);
        setEditHubs([]);
        setHubSearchText("");

        // Refetch the restricted hubs data to reflect the latest changes
        fetchRoHubAccessData();
      } else {
        message.error("Failed to update Restricted Hubs");
      }
    } catch (error) {
      console.error("Error updating restricted hubs:", error);
      message.error("An error occurred while updating restricted hubs");
    }
  };

  // --- Add Hub Modal Handlers ---
  const openAddHubModal = () => {
    const existingHubIds = editHubs.map(h => h.id);
    setTempSelectedHubs(existingHubIds);
    setIsAddHubModalVisible(true);
  };

  const closeAddHubModal = () => {
    setIsAddHubModalVisible(false);
    setTempSelectedHubs([]);
    setHubSelectionSearchText("");
  };

  const toggleHubSelection = (hubId) => {
    if (tempSelectedHubs.includes(hubId)) {
      setTempSelectedHubs(tempSelectedHubs.filter(id => id !== hubId));
    } else {
      setTempSelectedHubs([...tempSelectedHubs, hubId]);
    }
  };

  const handleAddAllHubs = (e) => {
    if (e.target.checked) {
      const hubsToAdd = allAvailableHubs
        .filter(hub => !tempSelectedHubs.includes(hub.id))
        .map(hub => hub.id);
      setTempSelectedHubs([...tempSelectedHubs, ...hubsToAdd]);
    } else {
      const filteredIds = allAvailableHubs.map(h => h.id);
      const newSelection = tempSelectedHubs.filter(id => !filteredIds.includes(id));
      setTempSelectedHubs(newSelection);
    }
  };

  const confirmAddHubs = () => {
    if (tempSelectedHubs.length === 0) {
      // Allow empty if they want to remove all restrictions
      // message.warning("At least one hub must be selected.");
    }
    const pool = {};
    editHubs.forEach(h => { pool[h.id] = h; });
    allAvailableHubs.forEach(h => { pool[h.id] = { id: h.id, name: h.name }; });
    const newEditHubs = tempSelectedHubs.map(id => pool[id]).filter(obj => obj);
    setEditHubs(newEditHubs);
    closeAddHubModal();
  };

  const columns = [
    {
      title: "Workflow",
      dataIndex: "title",
      key: "title",
      render: (text) => (
        <span style={{ color: "#101828", fontWeight: "500" }}>{text}</span>
      ),
    },
    {
      title: "Access status",
      key: "access_status",
      width: "15%",
      render: (_, record) => {
        const hasRestrictions = record.restricted_hubs && record.restricted_hubs.length > 0;
        return hasRestrictions ? (
          <Tag
            style={{
              color: "#3538CD",
              background: "#EEF4FF",
              border: "1px solid #C7D7FE",
              borderRadius: "16px",
              padding: "2px 10px",
              margin: 0,
              fontSize: "13px",
              fontWeight: 500,
            }}
          >
            Restricted
          </Tag>
        ) : (
          <Tag
            style={{
              color: "#344054",
              background: "#F9FAFB",
              border: "1px solid #EAECF0",
              borderRadius: "16px",
              padding: "2px 10px",
              margin: 0,
              fontSize: "13px",
              fontWeight: 500,
            }}
          >
            Open
          </Tag>
        );
      },
    },
    {
      title: "Restricted hubs",
      dataIndex: "restricted_hubs",
      key: "restricted_hubs",
      width: "40%",
      render: (hubs) => (
        <div style={{ display: "flex", gap: "8px", flexWrap: "nowrap", overflow: "hidden", alignItems: "center" }}>
          {hubs && hubs.length > 0 ? (
            <>
              {hubs.slice(0, 3).map((hub, index) => (
                <Tag
                  color="purple"
                  key={index}
                  style={{
                    color: "#3538CD",
                    background: "#EEF4FF",
                    border: "1px solid #C7D7FE",
                    borderRadius: "16px",
                    padding: "2px 10px",
                    margin: 0,
                    whiteSpace: "nowrap",
                  }}
                >
                  {hub.premise_name}
                </Tag>
              ))}
              {hubs.length > 3 && (
                <Tag
                  color="blue"
                  style={{
                    color: "#3538CD",
                    background: "#EEF4FF",
                    border: "1px solid #C7D7FE",
                    borderRadius: "16px",
                    padding: "2px 8px",
                    margin: 0,
                    cursor: "pointer",
                  }}
                  onClick={() => showAllHubs(hubs)}
                >
                  +{hubs.length - 3}
                </Tag>
              )}
            </>
          ) : (
            <span style={{ color: "#667085", fontSize: "14px" }}>No restrictions</span>
          )}
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, record) => (
        <Button
          icon={<EditOutlined style={{ color: "#667085" }} />}
          onClick={() => showEditModal(record)}
          style={{
            borderRadius: "8px",
            border: "1px solid #EAECF0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "36px",
            height: "36px",
          }}
        />
      ),
      align: "right",
    },
  ];

  const dataSource = result?.items || [];

  const filteredData = dataSource.filter((item) => {
    const searchLower = (searchText || "").toLowerCase();
    return (
      (item.title && item.title.toLowerCase().includes(searchLower))
    );
  });

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  return (
    <div style={{ padding: "20px", background: "#F8FAFC", minHeight: "100vh" }}>
      <div style={{ background: "#fff", padding: "16px 24px", borderRadius: "8px", boxShadow: "0 1px 2px rgba(16, 24, 40, 0.05)" }}>
        {/* Header */}
        <div style={{ marginBottom: "24px", display: "flex", alignItems: "center" }}>
          <img src={back_img} onClick={() => navigate(-1)} style={{ cursor: "pointer", marginRight: "8px" }} alt="back" />
          <h1 style={{ margin: 0, fontSize: "20px", fontWeight: "600", color: "#101828" }}>RO Hub Access</h1>
        </div>

        {/* Filters */}
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "24px" }}>
          <Input
            prefix={<SearchOutlined style={{ color: "#667085" }} />}
            placeholder="Search workflow"
            style={{ flex: 1, borderRadius: "8px" }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            className="custom-height-input"
          />
        </div>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={filteredData}
          pagination={pagination}
          onChange={handleTableChange}
          rowClassName="hub-access-table-row"
          rowKey="id"
          loading={{
            spinning: fetching,
            indicator: <img src={Smcs_Loader} style={{ height: "100px", width: "100px" }} alt="Loading..." />
          }}
        />

        {/* Modal for displaying all restricted hubs (View Only) */}
        <Modal
          title="Restricted Hubs"
          visible={isModalVisible}
          onCancel={handleCancel}
          footer={null}
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {selectedHubs.map((hub, index) => (
              <Tag color="purple" key={index} style={{ color: "#6941C6", background: "#F9F5FF", border: "1px solid #E9D7FE", borderRadius: "16px", padding: "4px 12px", margin: 0 }}>
                {hub.premise_name}
              </Tag>
            ))}
          </div>
        </Modal>

        {/* Modal for Editing Restricted Hubs */}
        <Modal
          title={`Restricted Hubs (${editHubs.length})`}
          visible={isEditModalVisible}
          onCancel={handleEditCancel}
          footer={null}
          width={600}
        >
          <div style={{ display: "flex", gap: "12px", marginBottom: "24px" }}>
            <Input
              placeholder="Search Hub..."
              style={{ borderRadius: "6px" }}
              value={hubSearchText}
              onChange={(e) => setHubSearchText(e.target.value)}
              allowClear
            />
            <Button
              type="primary"
              style={{ background: "#175EB6", borderRadius: "6px" }}
              icon={<img src={AddCircleWhite} alt="add" style={{ marginRight: "5px" }} />}
              onClick={openAddHubModal}
            >
              Add Hub
            </Button>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "24px", maxHeight: "300px", overflowY: "auto" }}>
            {editHubs
              .filter((hub) => (hub?.name || "").toLowerCase().includes((hubSearchText || "").toLowerCase()))
              .map((hub, index) => (
                <Tag
                  key={index}
                  closable
                  onClose={(e) => {
                    e.preventDefault();
                    handleRemoveHub(hub);
                  }}
                  style={{
                    color: "#6941C6",
                    background: "#F9F5FF",
                    border: "1px solid #E9D7FE",
                    borderRadius: "16px",
                    padding: "6px 14px",
                    margin: 0,
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px"
                  }}
                >
                  {hub.name}
                </Tag>
              ))}
          </div>

          <Button type="primary" block style={{ height: "44px", borderRadius: "8px", background: "#175EB6", fontSize: "16px", fontWeight: "600" }} onClick={handleSaveChanges}>
            Save Changes
          </Button>
        </Modal>

        {/* Modal for Selecting Hubs to Add */}
        <Modal
          title={null}
          visible={isAddHubModalVisible}
          onCancel={closeAddHubModal}
          footer={null}
          width={800}
          bodyStyle={{ padding: "24px" }}
        >
          <div style={{ display: "flex", gap: "16px", marginBottom: "24px", marginTop: "16px" }}>
            <Input
              prefix={<SearchOutlined style={{ color: "#98A2B3" }} />}
              placeholder="Search"
              value={hubSelectionSearchText}
              onChange={(e) => setHubSelectionSearchText(e.target.value)}
              style={{ flex: 1, borderRadius: "8px" }}
              allowClear
              className="custom-height-input"
            />
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <span style={{ fontWeight: "600", fontSize: "16px", color: "#101828" }}>
              All Hub ({allAvailableHubs.length})
            </span>
            <Checkbox
              onChange={handleAddAllHubs}
              checked={allAvailableHubs.length > 0 && allAvailableHubs.every(hub => tempSelectedHubs.includes(hub.id))}
            >
              <span style={{ color: "#175EB6", fontWeight: "600" }}>Add All Hubs</span>
            </Checkbox>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", maxHeight: "400px", overflowY: "auto", marginBottom: "24px" }}>
            {allAvailableHubs.map((hub, index) => {
              const isSelected = tempSelectedHubs.includes(hub.id);
              return (
                <Tag
                  key={index}
                  onClick={() => toggleHubSelection(hub.id)}
                  style={{
                    cursor: "pointer",
                    padding: "6px 14px",
                    borderRadius: "16px",
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    border: isSelected ? "1px solid #D1FADF" : "1px solid #F2F4F7",
                    background: isSelected ? "#ECFDF3" : "#F8FAFC",
                    color: isSelected ? "#027A48" : "#344054",
                    margin: 0
                  }}
                >
                  {hub.name}
                  {isSelected ? <CloseOutlined style={{ fontSize: "12px" }} /> : <PlusCircleOutlined style={{ fontSize: "14px", color: "#98A2B3" }} />}
                </Tag>
              );
            })}
          </div>

          <Button
            type="primary"
            block
            style={{ height: "44px", borderRadius: "8px", background: "#175EB6", fontSize: "16px", fontWeight: "600" }}
            onClick={confirmAddHubs}
          >
            Add Hubs
          </Button>
        </Modal>
      </div>
    </div>
  );
};

export default RoHubAccess;
